import { useCallback, useEffect, useState } from 'react'

import { useNetworkDebugSource } from 'shared/lib/networkDebug'

import { fetchAcknowledgmentStatusData } from '../api/fetchAcknowledgmentStatusData'

import type { AcknowledgmentCommitmentItem, AcknowledgmentOffering } from './types'

type UseAcknowledgmentStatusDataParams = {
  show: boolean
  partnerId: string
  fYear: number
  programType: AcknowledgmentOffering
}

export const useAcknowledgmentStatusData = ({
  show,
  partnerId,
  fYear,
  programType,
}: UseAcknowledgmentStatusDataParams) => {
  const debugSource = useNetworkDebugSource()
  const [items, setItems] = useState<AcknowledgmentCommitmentItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  /* Set once the first read has answered, so a host acting on the rows tells "no rows" from "not
     read yet". */
  const [hasLoaded, setHasLoaded] = useState(false)

  const load = useCallback(async () => {
    if (!show || !partnerId || !fYear || programType === '') {
      setItems([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetchAcknowledgmentStatusData({
        partnerId,
        fYear,
        programType,
        debugSource,
      })

      setItems(response)
    } catch (loadError) {
      setItems([])
      setError(loadError instanceof Error ? loadError : new Error())
    } finally {
      setIsLoading(false)
      setHasLoaded(true)
    }
  }, [debugSource, fYear, partnerId, programType, show])

  useEffect(() => {
    void load()
  }, [load])

  return {
    items,
    isLoading,
    hasLoaded,
    error,
    reload: load,
  }
}
